import { useMemo, useState } from "react";
import type { Chapter, Comic } from "@/types/comic";
import { buildChapters } from "@/utils/comics";

interface ChapterListProps {
  comic: Comic;
  onSelect: (chapter: Chapter) => void;
}

const COLLAPSED_COUNT = 12;

export default function ChapterList({ comic, onSelect }: ChapterListProps) {
  const [expanded, setExpanded] = useState(false);
  const [ascending, setAscending] = useState(false);

  const chapters = useMemo(() => buildChapters(comic), [comic]);
  const ordered = ascending ? [...chapters].reverse() : chapters;
  const visible = expanded ? ordered : ordered.slice(0, COLLAPSED_COUNT);

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 p-4 md:p-5">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
          <i className="ri-list-ordered text-lg text-primary-500"></i>
          章节列表
          <span className="rounded bg-background-100 px-1.5 py-0.5 text-[11px] font-medium text-foreground-500">
            共 {comic.chapterCount} 话
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAscending((value) => !value)}
            className="flex h-8 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-2.5 text-[11px] font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-sort-asc text-sm"></i>
            {ascending ? "正序" : "倒序"}
          </button>
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="flex h-8 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-2.5 text-[11px] font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className={`${expanded ? "ri-collapse-vertical-line" : "ri-expand-vertical-line"} text-sm`}></i>
            {expanded ? "收起" : "展开全部"}
          </button>
        </div>
      </div>

      <div className="divide-y divide-background-200">
        {visible.map((chapter, index) => (
          <button
            key={chapter.id}
            type="button"
            onClick={() => onSelect(chapter)}
            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-background-100 md:px-5"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-background-100 font-heading text-[11px] font-semibold text-foreground-500">
              {ascending ? comic.chapterCount - index : index + 1}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-foreground-900">
                {chapter.title}
              </span>
              <span className="mt-0.5 block text-[11px] text-foreground-400">
                更新于 {chapter.updatedAt}
              </span>
            </span>
            {index === 0 && !ascending && (
              <span className="shrink-0 rounded bg-primary-100 px-1.5 py-0.5 text-[10px] font-semibold text-primary-800">
                最新
              </span>
            )}
            <i className="ri-arrow-right-s-line shrink-0 text-foreground-300"></i>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-background-200 p-4">
        <p className="text-[11px] text-foreground-400">
          已展示 {visible.length} / {comic.chapterCount} 话，章节阅读功能正在开发中
        </p>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="flex items-center gap-1 whitespace-nowrap text-[11px] font-medium text-primary-700 hover:text-primary-800"
        >
          {expanded ? "收起章节" : "展开全部章节"}
          <i className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
        </button>
      </div>
    </section>
  );
}